import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Card, CardBody, CardHeader, Label, Spinner } from "reactstrap";
import { useSelector } from "react-redux";
import Select from "react-select";

import storeService from "../../redux/services/store.service";
import { isSuperAdminUser } from "../../helpers/isSuperAdmin";
import { authHeader } from "../../redux/services/auth-header";
import axios from "axios";
import dayjs from "dayjs";
import DurationFilter from "../Common/DurationFilter";
import TotalSalesOverview from "../charts/TotalSalesOverview";
import EarningsBreakdown from "../charts/EarningsBreakdown";
import TopSellingProducts from "../charts/TopSellingProducts";
import SalesByCategory from "../charts/SalesByCategory";
import MonthlyEarnings from "../charts/MonthlyEarnings";
import OrderStatusDistribution from "../charts/OrderStatusDistribution";

const DashboardContainer = () => {
    const permState = useSelector((s) => s.permissions);
    const { user: currentUser } = useSelector((s) => s.auth);
    const isSuperAdmin = isSuperAdminUser(currentUser, permState);

    const [duration, setDuration] = useState("month");
    const [customRange, setCustomRange] = useState({
        from: dayjs().subtract(29, "day").format("YYYY-MM-DD"),
        to: dayjs().format("YYYY-MM-DD"),
    });

    const { from, to } = useMemo(() => {
        const today = dayjs().format("YYYY-MM-DD");
        if (duration === "today") return { from: today, to: today };
        if (duration === "yesterday") {
            const y = dayjs().subtract(1, "day").format("YYYY-MM-DD");
            return { from: y, to: y };
        }
        if (duration === "7d") return { from: dayjs().subtract(6, "day").format("YYYY-MM-DD"), to: today };
        if (duration === "month") return { from: dayjs().startOf("month").format("YYYY-MM-DD"), to: today };
        if (duration === "6month") return { from: dayjs().subtract(6, "month").format("YYYY-MM-DD"), to: today };
        return { from: customRange.from || "", to: customRange.to || "" };
    }, [duration, customRange.from, customRange.to]);

    const [stores, setStores] = useState([]);
    const [storesLoading, setStoresLoading] = useState(false);
    const [selectedStore, setSelectedStore] = useState(null);

    // POS widgets (store-aware)
    const [posLoading, setPosLoading] = useState(false);
    const [posTotals, setPosTotals] = useState(null);
    const [posChart, setPosChart] = useState(null);

    const storeId = useMemo(() => {
        if (!isSuperAdmin) return null;
        return selectedStore?.value ?? null;
    }, [isSuperAdmin, selectedStore]);

    useEffect(() => {
        if (!isSuperAdmin) {
            setStores([]);
            setSelectedStore(null);
            return;
        }
        let alive = true;
        (async () => {
            setStoresLoading(true);
            try {
                const res = await storeService.list();
                const data = res.data?.data;
                if (!alive) return;
                const opts = (Array.isArray(data) ? data : []).map((s) => ({
                    label: s.name || `Store ${s.id}`,
                    value: s.id,
                }));
                setStores(opts);
            } catch {
                if (!alive) return;
                setStores([]);
            } finally {
                if (alive) setStoresLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [isSuperAdmin]);

    // POS endpoints use filter/customStart/customEnd (not from/to). Auto-refresh when filters change.
    useEffect(() => {
        let alive = true;
        (async () => {
            setPosLoading(true);
            try {
                const baseTotals = `${process.env.REACT_APP_API_URL}sales/total-sales`;
                const baseChart = `${process.env.REACT_APP_API_URL}sales/chart-data`;

                const params = new URLSearchParams();
                const hasRange = Boolean(from) && Boolean(to);
                params.set("filter", hasRange ? "custom" : "last_7_days");
                if (hasRange) {
                    params.set("customStart", from);
                    params.set("customEnd", to);
                }
                if (storeId != null) params.set("storeId", String(storeId));

                const [totalsRes, chartRes] = await Promise.all([
                    axios.get(`${baseTotals}?${params.toString()}`, { headers: authHeader() }),
                    axios.get(`${baseChart}?${params.toString()}`, { headers: authHeader() }),
                ]);
                if (!alive) return;
                setPosTotals(totalsRes.data);
                setPosChart(chartRes.data);
            } catch {
                if (!alive) return;
                setPosTotals(null);
                setPosChart(null);
            } finally {
                if (alive) setPosLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [from, to, storeId]);

    return (
        <Container fluid className="mt-4">
            <Card className="mb-4 shadow-sm border-0">
                <CardHeader className="bg-white border-0">
                    <div className="row g-3 align-items-end">
                        <div className="col-12 col-lg-4">
                            <div className="fw-semibold">Analytics filters</div>
                            <div className="small text-muted">Preset + store (super admin only)</div>
                        </div>
                        <div className="col-12 col-lg-8">
                            <div className="d-flex flex-column flex-xl-row gap-3 align-items-stretch align-items-xl-end">
                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                    <DurationFilter
                                        duration={duration}
                                        setDuration={setDuration}
                                        customRange={customRange}
                                        setCustomRange={setCustomRange}
                                        label="Date range"
                                    />
                                </div>
                                {isSuperAdmin ? (
                                    <div className="w-100" style={{ minWidth: 0, maxWidth: "100%" }}>
                                        <Label className="small text-muted fw-bold mb-1 d-block">Store</Label>
                                        <Select
                                            classNamePrefix="react-select"
                                            className="w-100"
                                            isClearable
                                            isLoading={storesLoading}
                                            options={stores}
                                            value={selectedStore}
                                            onChange={(v) => setSelectedStore(v)}
                                            placeholder="All stores"
                                        />
                                    </div>
                                ) : null}
                                {posLoading ? (
                                    <div className="d-flex align-items-center text-muted small flex-shrink-0 py-1">
                                        <Spinner size="sm" className="me-2" /> Updating…
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardBody className="pt-0">
                    <Row className="g-2">
                        <Col md={6} lg={4}>
                            <Card className="border-0 shadow-sm">
                                <CardBody>
                                    <div className="small text-muted fw-bold text-uppercase">POS Total Amount</div>
                                    <div className="h4 mb-0">{posTotals?.totalAmount ?? 0}</div>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md={6} lg={4}>
                            <Card className="border-0 shadow-sm">
                                <CardBody>
                                    <div className="small text-muted fw-bold text-uppercase">POS Total Sales</div>
                                    <div className="h4 mb-0">{posTotals?.totalSales ?? 0}</div>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md={12} lg={4}>
                            <Card className="border-0 shadow-sm">
                                <CardBody>
                                    <div className="small text-muted fw-bold text-uppercase">POS Trend (labels)</div>
                                    <div className="small text-muted text-truncate">
                                        {(posChart?.labels || []).slice(0, 8).join(", ") || "—"}
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
            <Row>
                <Col md={8}>
                    <TotalSalesOverview from={from} to={to} storeId={storeId} />
                </Col>
                <Col md={4}>
                    <EarningsBreakdown from={from} to={to} storeId={storeId} />
                </Col>
            </Row>

            <Row>
                <Col md={6}><TopSellingProducts from={from} to={to} storeId={storeId} /></Col>
                <Col md={6}><SalesByCategory from={from} to={to} storeId={storeId} /></Col>
            </Row>

            <Row>
                <Col md={6}><MonthlyEarnings from={from} to={to} storeId={storeId} /></Col>
                <Col md={6}><OrderStatusDistribution from={from} to={to} storeId={storeId} /></Col>
            </Row>

        </Container>
    );
};

export default DashboardContainer;
